import moment from 'moment';
import { useEffect, useMemo, useRef } from 'react';
import {
  BLOCK_TIME_STEP_MINUTES,
  BOOKING_FORM_NO_TIME,
  bookingFieldDateKey,
  buildBlockEndTimeOptions,
  buildBlockStartTimeOptions,
  findBlockIntervalForStart,
  getCheckoutDatesFromEndOptions,
  isDateOnClosedScheduleDay,
  isUnsetTime,
  resolveAdvanceBookingLimitDays
} from './bookingFormHelpers';

/**
 * Slot/calendar orchestration for AddBlockV2 and EditBlockV2.
 */
export function useBlockDetailForm({
  amenity,
  availabilitySlots,
  watchStartTime,
  watchEndDate,
  watchStartDate,
  watchEndTime,
  watchAmenityId,
  watchBuilding,
  setValue,
  clearErrors,
  isFormInitialized = true
}) {
  const selectedInterval = useMemo(() => {
    if (isUnsetTime(watchStartTime) || typeof watchStartTime !== 'string') return null;
    return findBlockIntervalForStart(availabilitySlots, watchStartTime);
  }, [availabilitySlots, watchStartTime]);

  const blockMaxEndMoment = useMemo(() => {
    if (!selectedInterval?.max_end_time) return null;
    const m = moment.parseZone(selectedInterval.max_end_time);
    return m.isValid() ? m : null;
  }, [selectedInterval]);

  const startTimeOptions = useMemo(() => {
    const startYmd = bookingFieldDateKey(watchStartDate);
    if (!startYmd || !Array.isArray(availabilitySlots) || availabilitySlots.length === 0) {
      return [];
    }
    return buildBlockStartTimeOptions(availabilitySlots, startYmd);
  }, [availabilitySlots, watchStartDate]);

  const allEndTimeOptions = useMemo(() => {
    if (isUnsetTime(watchStartTime) || typeof watchStartTime !== 'string') return [];
    if (!selectedInterval?.max_end_time) return [];

    return buildBlockEndTimeOptions(
      watchStartTime,
      selectedInterval.max_end_time,
      BLOCK_TIME_STEP_MINUTES
    );
  }, [selectedInterval, watchStartTime]);

  const validEndDateKeys = useMemo(
    () => getCheckoutDatesFromEndOptions(allEndTimeOptions),
    [allEndTimeOptions]
  );

  const endTimeOptions = useMemo(() => {
    const endYmd = bookingFieldDateKey(watchEndDate);
    if (!endYmd) return [];

    return allEndTimeOptions.filter(
      (iso) => moment.parseZone(iso).format('YYYY-MM-DD') === endYmd
    );
  }, [allEndTimeOptions, watchEndDate]);

  const disabledDates = (current) => {
    if (amenity && isDateOnClosedScheduleDay(current, amenity.amenities_schedules)) {
      return true;
    }

    if (current && current.format('YYYY-MM-DD') === moment().format('YYYY-MM-DD')) {
      return false;
    }

    const limitDays = resolveAdvanceBookingLimitDays(amenity);
    const maxBookable = moment().startOf('day').add(limitDays, 'days');
    if (current && current.startOf('day').isAfter(maxBookable)) {
      return true;
    }

    const today = moment().endOf('day');
    return current && current.isSameOrBefore(today);
  };

  const disabledEndDate = (current) => {
    if (watchStartDate && current && moment(current).isBefore(moment(watchStartDate), 'day')) {
      return true;
    }

    if (isUnsetTime(watchStartTime) || typeof watchStartTime !== 'string') {
      return true;
    }

    if (!blockMaxEndMoment?.isValid()) return true;

    const startDateMoment = moment(watchStartDate);
    const currentMoment = moment(current);
    const maxEndDayStart = blockMaxEndMoment.clone().startOf('day');

    const isBeforeStartDate = currentMoment.isBefore(startDateMoment, 'day');
    const isAfterMaxEndDate = currentMoment.startOf('day').isAfter(maxEndDayStart);

    if (isBeforeStartDate || isAfterMaxEndDate) {
      return true;
    }

    if (validEndDateKeys.size > 0) {
      const ymd = current.format('YYYY-MM-DD');
      if (!validEndDateKeys.has(ymd)) {
        return true;
      }
    }

    return false;
  };

  const prevStartDate = useRef(watchStartDate);
  const prevStartTime = useRef(watchStartTime);
  const prevEndDate = useRef(watchEndDate);
  const prevAmenityId = useRef(watchAmenityId);
  const prevBuilding = useRef(watchBuilding);
  const wasFormInitializedRef = useRef(isFormInitialized);
  const isFirstRenderRef = useRef(true);

  useEffect(() => {
    if (isFirstRenderRef.current || !isFormInitialized) {
      prevStartDate.current = watchStartDate;
      prevStartTime.current = watchStartTime;
      prevEndDate.current = watchEndDate;
      prevAmenityId.current = watchAmenityId;
      prevBuilding.current = watchBuilding;
      wasFormInitializedRef.current = isFormInitialized;
      isFirstRenderRef.current = false;
      return;
    }

    if (!wasFormInitializedRef.current && isFormInitialized) {
      prevStartDate.current = watchStartDate;
      prevStartTime.current = watchStartTime;
      prevEndDate.current = watchEndDate;
      prevAmenityId.current = watchAmenityId;
      prevBuilding.current = watchBuilding;
      wasFormInitializedRef.current = true;
    }
  }, [isFormInitialized, watchStartDate, watchStartTime, watchEndDate, watchAmenityId, watchBuilding]);

  const clearCascadeErrors = (fields) => {
    if (clearErrors) clearErrors(fields);
  };

  useEffect(() => {
    if (!isFormInitialized) return;
    if (!watchBuilding) return;

    const buildingChanged =
      prevBuilding.current != null && watchBuilding?.value !== prevBuilding.current?.value;

    if (buildingChanged) {
      setValue('amenity_id', undefined, { shouldValidate: false, shouldDirty: true });
      setValue('startDate', null, { shouldValidate: false, shouldDirty: true });
      setValue('startTime', BOOKING_FORM_NO_TIME, { shouldValidate: false, shouldDirty: true });
      setValue('endTime', BOOKING_FORM_NO_TIME, { shouldValidate: false, shouldDirty: true });
      setValue('endDate', null, { shouldValidate: false, shouldDirty: true });

      prevAmenityId.current = undefined;
      prevStartDate.current = null;
      prevStartTime.current = BOOKING_FORM_NO_TIME;
      prevEndDate.current = null;

      clearCascadeErrors(['amenity_id', 'startDate', 'startTime', 'endTime', 'endDate']);
    }

    prevBuilding.current = watchBuilding;
  }, [isFormInitialized, watchBuilding, setValue, clearErrors]);

  useEffect(() => {
    if (!isFormInitialized) return;

    const amenityChanged =
      prevAmenityId.current != null && watchAmenityId !== prevAmenityId.current;
    const startDateKey = bookingFieldDateKey(watchStartDate);
    const prevStartDateKey = bookingFieldDateKey(prevStartDate.current);
    const startDateChanged = startDateKey !== prevStartDateKey;

    if (amenityChanged || startDateChanged) {
      setValue('startTime', BOOKING_FORM_NO_TIME, { shouldValidate: false, shouldDirty: true });
      setValue('endTime', BOOKING_FORM_NO_TIME, { shouldValidate: false, shouldDirty: true });
      prevStartTime.current = BOOKING_FORM_NO_TIME;
      setValue('endDate', null, { shouldValidate: false, shouldDirty: true });
      prevEndDate.current = null;

      if (amenityChanged) {
        setValue('startDate', null, { shouldValidate: false, shouldDirty: true });
        prevStartDate.current = null;
        clearCascadeErrors(['startTime', 'endTime', 'endDate', 'startDate']);
      } else {
        prevStartDate.current = watchStartDate;
        clearCascadeErrors(['startTime', 'endTime', 'endDate']);
      }

      prevAmenityId.current = watchAmenityId;
    }
  }, [isFormInitialized, watchStartDate, watchAmenityId, setValue, clearErrors]);

  useEffect(() => {
    if (!isFormInitialized) return;

    const startTimeChanged = watchStartTime !== prevStartTime.current;
    const endDateKey = bookingFieldDateKey(watchEndDate);
    const prevEndDateKey = bookingFieldDateKey(prevEndDate.current);
    const endDateChanged =
      endDateKey !== prevEndDateKey && (endDateKey !== '' || prevEndDateKey !== '');

    if (startTimeChanged || endDateChanged) {
      setValue('endTime', BOOKING_FORM_NO_TIME, { shouldValidate: false, shouldDirty: true });

      if (startTimeChanged) {
        clearCascadeErrors(['endTime', 'startTime']);
      } else {
        clearCascadeErrors(['endTime']);
      }

      let wipedEndDate = false;
      if (startTimeChanged && watchEndDate && blockMaxEndMoment) {
        if (moment(watchEndDate).startOf('day').isAfter(blockMaxEndMoment, 'day')) {
          setValue('endDate', null, { shouldValidate: false, shouldDirty: true });
          clearCascadeErrors(['endDate']);
          wipedEndDate = true;
        }
      }

      prevStartTime.current = watchStartTime;
      prevEndDate.current = wipedEndDate ? null : watchEndDate;
    }
  }, [
    isFormInitialized,
    watchStartTime,
    watchEndDate,
    blockMaxEndMoment,
    setValue,
    clearErrors
  ]);

  useEffect(() => {
    if (!isFormInitialized) return;
    if (!blockMaxEndMoment?.isValid() || !watchEndDate) return;
    if (moment(watchEndDate).isAfter(blockMaxEndMoment, 'day')) {
      setValue('endDate', blockMaxEndMoment.clone(), {
        shouldDirty: true,
        shouldValidate: false
      });
      clearCascadeErrors('endDate');
    }
  }, [isFormInitialized, blockMaxEndMoment, watchEndDate, setValue, clearErrors]);

  useEffect(() => {
    if (!isFormInitialized) return;
    if (!blockMaxEndMoment?.isValid()) return;
    if (isUnsetTime(watchEndTime) || typeof watchEndTime !== 'string') return;
    const endM = moment.parseZone(watchEndTime);
    if (!endM.isValid()) return;
    if (endM.isAfter(blockMaxEndMoment)) {
      setValue('endTime', BOOKING_FORM_NO_TIME, { shouldDirty: true, shouldValidate: false });
      clearCascadeErrors('endTime');
    }
  }, [isFormInitialized, blockMaxEndMoment, setValue, watchEndTime, watchStartTime, clearErrors]);

  return {
    selectedInterval,
    blockMaxEndMoment,
    startTimeOptions,
    endTimeOptions,
    disabledDates,
    disabledEndDate
  };
}
